import { Injectable, inject } from '@angular/core';
import { Observable, of, map, catchError, switchMap, take } from 'rxjs';
import { GravatarResponse, GravatarService } from "../../../services/gravatar.service";
import { IAppGlobalSettings, ModelService } from "core-client/api";

@Injectable({
	providedIn: 'root'
})
export class AvatarService {
	private readonly gravatarService = inject(GravatarService);
	private readonly modelService = inject(ModelService);

	/**
	 * Get avatar URL with priority:
	 * 1. Internal avatar (if exists)
	 * 2. Gravatar (if exists AND enabled in settings)
	 * 3. null (component will show initials)
	 *
	 */
	getAvatarUrl(
		email: string,
		internalAvatarUrl?: string | null,
		size: number = 40
	): Observable<string | null> {
		// Priority 1: Use internal avatar if available
		if (internalAvatarUrl) {
			return of(internalAvatarUrl);
		}

		// Priority 2: Check Gravatar (only if enabled in settings)
		if (!email) {
			return of(null);
		}

		// SwitchMap to re-check Gravatar whenever settings change
		return this.modelService._appGlobalSettings.appGlobalSettings$.pipe(
			switchMap((settings: IAppGlobalSettings | null) => {
				if (!settings?.user_profile.gravatar.enabled) {
					return of(null);
				}

				// If Gravatar is enabled, check if it exists
				return this.checkGravatar(email, size).pipe(
					map(gravatarResponse => {
						if (gravatarResponse.exists) {
							return gravatarResponse.url;
						}
						return null;
					})
				);
			}),
			catchError(error => {
				console.error('[AvatarService] Error:', error);
				return of(null);
			})
		);
	}

	/**
	 * Check if Gravatar exists for email
	 * This calls the backend which checks Gravatar.com
	 */
	private checkGravatar(email: string, size: number): Observable<GravatarResponse> {
		return this.gravatarService.getGravatarInfo(email, {
			size
		}).pipe(
			catchError(() => of({
				url: '',
				email,
				cached: false,
				exists: false
			}))
		);
	}

	computeInitials(name?: string | null, email?: string | null): string {
		if (name) {
			const parts = name.split(' ').filter(n => n.length > 0);
			if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
			if (parts.length === 1) return parts[0][0].toUpperCase();
		}
		if (email) return email[0].toUpperCase();
		return '?';
	}

	generateInitialsDataUrl(name?: string | null, email?: string | null, size: number = 96): string {
		const initials = this.computeInitials(name, email);
		const canvas = document.createElement('canvas');
		canvas.width = size;
		canvas.height = size;
		const ctx = canvas.getContext('2d');
		if (!ctx) return '';
		const styles = getComputedStyle(document.documentElement);
		const bg = styles.getPropertyValue('--adx-color-primary-shade-14-background').trim() || '#003D6A';
		const fg = styles.getPropertyValue('--adx-color-primary-inverted-text').trim() || '#FFFFFF';
		ctx.fillStyle = bg;
		ctx.beginPath();
		ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
		ctx.fill();
		ctx.fillStyle = fg;
		ctx.font = `${Math.floor(size / 2.5)}px sans-serif`;
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillText(initials, size / 2, size / 2);
		return canvas.toDataURL('image/png');
	}

	/**
	 * Prefetch avatar for better performance
	 */
	prefetchAvatar(email: string, internalAvatarUrl?: string | null, size: number = 40): void {
		this.getAvatarUrl(email, internalAvatarUrl, size).pipe(take(1)).subscribe();
	}

	/**
	 * Clear cache
	 */
	clearCache(email?: string): void {
		this.gravatarService.clearCache(email);
	}
}
